import { useId, type ReactNode, type ReactElement, cloneElement } from 'react';
import { fieldDescId } from './fieldDescId';

interface FieldProps {
  /** Label text displayed above the input. */
  label: ReactNode;
  /** Optional helper text shown below the input. */
  hint?: ReactNode;
  /** Optional error message shown below the input. Replaces hint when present. */
  error?: ReactNode;
  /** Visual styling — `daisyui` matches the existing `<label className="label">` pattern. */
  variant?: 'daisyui' | 'plain';
  /** Wrapper className for the outer container. */
  className?: string;
  /** Override the inner `<label>` className (defaults vary by `variant`). */
  labelClassName?: string;
  /** Override the inner `<span>` wrapping the label text (daisyui variant only). */
  labelTextClassName?: string;
  /**
   * Override the hint/error `<span>` className. The `plain` variant ships no
   * styling of its own, so a caller using `hint` there has to supply this or
   * the text renders unstyled.
   */
  hintClassName?: string;
  /**
   * Explicit id of the control to associate with the label. Use this when the
   * direct child is a wrapper (e.g. `<div className="relative">` with an icon
   * overlay) rather than the control itself. When supplied, the wrapper child
   * is NOT cloned with an id; the label's `htmlFor` points at `controlId`.
   */
  controlId?: string;
  /** The form control. Must accept an `id` prop. Receives auto-generated id via cloneElement. */
  children: ReactElement<{ id?: string; 'aria-describedby'?: string }>;
}

/**
 * Accessible form field wrapper that auto-associates a `<label>` with its
 * control via a generated `id` / `htmlFor` pair. Eliminates the manual id
 * threading that's the source of nearly all `jsx-a11y/label-has-for` and
 * `jsx-a11y/label-has-associated-control` warnings.
 *
 * Usage:
 *   <Field label="Credits Used">
 *     <input type="number" value={x} onChange={onX} className="input" />
 *   </Field>
 *
 * For inputs wrapped in a positional container (icon overlays etc.), pass the
 * control's id via `controlId`:
 *   <Field label="Email" controlId="email">
 *     <div className="relative">
 *       <input id="email" type="email" ... />
 *       <FiMail className="absolute ..." />
 *     </div>
 *   </Field>
 *
 * The child receives an auto-generated `id`. If the child already specifies
 * an `id`, that one is preserved (so existing code can opt out incrementally).
 * `hint` / `error` text is wired via `aria-describedby` for screen readers.
 */
export default function Field({
  label,
  hint,
  error,
  variant = 'daisyui',
  className,
  labelClassName,
  labelTextClassName,
  hintClassName,
  controlId,
  children,
}: FieldProps) {
  const reactId = useId();
  const inputId = controlId ?? children.props.id ?? `field-${reactId}`;
  const descId = hint || error ? fieldDescId(inputId) : undefined;

  // Merge our hint/error descriptor id with any aria-describedby the child
  // already declares (e.g. a custom tooltip), rather than clobbering it.
  const mergedDescribedBy = descId
    ? [children.props['aria-describedby'], descId].filter(Boolean).join(' ')
    : children.props['aria-describedby'];

  // When controlId is supplied, the child is treated as a wrapper — we don't
  // overwrite its id. Otherwise the child IS the control and gets the id.
  const cloned = controlId
    ? children
    : cloneElement(children, {
        id: inputId,
        ...(mergedDescribedBy ? { 'aria-describedby': mergedDescribedBy } : {}),
      });

  if (variant === 'daisyui') {
    return (
      <div className={className ?? 'form-control'}>
        <label htmlFor={inputId} className={labelClassName ?? 'label py-1'}>
          <span className={labelTextClassName ?? 'label-text text-xs'}>{label}</span>
        </label>
        {cloned}
        {hint || error ? (
          <span
            id={descId}
            className={
              hintClassName ??
              `label-text-alt text-xs mt-1 ${error ? 'text-error' : 'text-base-content/60'}`
            }
          >
            {error ?? hint}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className={className}>
      <label htmlFor={inputId} className={labelClassName}>
        {label}
      </label>
      {cloned}
      {hint || error ? (
        <span id={descId} className={hintClassName} aria-live={error ? 'polite' : undefined}>
          {error ?? hint}
        </span>
      ) : null}
    </div>
  );
}
